import React from "react";
import { useSelector } from "react-redux";
import { Transition, TransitionGroup } from "react-transition-group";
import loadable from "@loadable/component";
import PropTypes from "prop-types";
import { injectModels } from "core-web/state";
import PopupWrapper from "theme/components/Popup/PopupWrapper";

const availablePopups = {
    addedToBasketPopup: loadable(() => import("theme/components/Popup/popupComponents/AddedToBasketPopup")),
    notificationPopup: loadable(() => import("theme/components/Popup/popupComponents/NotificationPopup")),
};

/**
 * Popups are used to display content above the page without "locking" the page.
 * Therefore other elements on the page can still be interacted with.
 * There can be several popups active at a time.
 * Popup data is stored in redux state popups.
 */

const Popups = ({ unmountDelay }) => {
    const currentPopups = useSelector(({ popups }) => popups.current);
    const data = useSelector(({ popups }) => popups.data);
    /**
     * Render and animate popups
     */

    // Loop through all active popups
    const toRender = currentPopups.map((popupName) => {
        // Get the wrapper that should be used for this popup, defualts to PopupWrapper
        const Wrapper =
            data.hasOwnProperty(popupName) && data[popupName].wrapper ? data[popupName].wrapper : PopupWrapper;

        // Get the content component that should be used for this popup
        const Component = popupName && availablePopups.hasOwnProperty(popupName) ? availablePopups[popupName] : null;

        // Add a transition element to each popup to animate entering and exit and removed
        return (
            <Transition
                mountOnEnter
                unmountOnExit
                enter
                key={popupName}
                in={!!Component}
                timeout={{ appear: 0, exit: unmountDelay }}
            >
                {(transitionState) => (
                    <Wrapper className={transitionState} popupName={popupName} {...(data[popupName] || {})}>
                        <Component popupName={popupName} {...(data[popupName] || {})} />
                    </Wrapper>
                )}
            </Transition>
        );
    });

    // Renders each popup inside a TransitionGroup so that they also animates on exit when it's unmounted
    return <TransitionGroup>{toRender}</TransitionGroup>;
};

Popups.propTypes = {
    unmountDelay: PropTypes.number,
};

Popups.defaultProps = {
    unmountDelay: 50,
};

export default injectModels(["application", "popups"])(Popups);
